/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { 
  useGridApiRef, 
  GridToolbar,
  gridClasses,
  GridLogicOperator,
  GridCsvExportOptions
} from '@mui/x-data-grid';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { alpha, styled } from '@mui/material/styles';
import { Box, Chip, Typography } from '@mui/material';
import { IconButton } from '@mui/material'; 
import PreviewIcon from '@mui/icons-material/Preview';

import {
  listBaseChances,
  setBaseFilters,
} from '../../modules/chanceModule/chanceBases';
import { getNumberComma } from '../../utils/numberUtils';
import CstStatusChip from '../../components/common/datagrid/CstStatusChip';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { ChanceListBox } from '../../components/chancemng/ChanceListBox';
import ChanceStatusList from '../../components/chancemng/info/ChanceStatusList';
import ChanceList from '../../components/chancemng/ChanceList';
import ChanceTotalSummary from '../../components/chancemng/ChanceTotalSummary';
import ShareLinkBox from '../../components/common/ShareLinkBox';

// 짝수/홀수 행 스타일링을 위한 스타일 컴포넌트
const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(ChanceList)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[100],
    '&:hover, &.Mui-hovered': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY + theme.palette.action.selectedOpacity),
      '&:hover, &.Mui-hovered': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
        ),
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
  '& .amount-cell': {
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  '& .positive-amount': {
    color: theme.palette.success.dark,
  },
  '& .negative-amount': {
    color: theme.palette.error.dark,
  },
  '& .super-app-theme--header': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    fontWeight: 'bold',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-sortIcon': {
    color: theme.palette.primary.main,
  },
  '& .MuiDataGrid-menuIconButton': {
    color: theme.palette.primary.main,
  },
}));

const ChanceListContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { chances, defaultFilters, error, loading, totals } = useSelector(
    ({ chanceModule, loading }) => ({
      chances: chanceModule.chanceBases.chances,
      totals: chanceModule.chanceBases.totals,
      defaultFilters: chanceModule.chanceBases.defaultFilters,
      error: chanceModule.chanceBases.error,
      loading: loading['chance/LIST_BASE_CHANCES'],
    }),
  );

  const [tableData, setTableData] = useState([]);
  // 컬럼 가시성 상태 관리
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    id: false,
  });
  // 페이지네이션 상태 관리 추가
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  // 필터링 상태 관리 추가
  const [filterModel, setFilterModel] = useState({
    items: [],
    logicOperator: GridLogicOperator.And,
  });
  // 정렬 상태 관리
  const [sortModel, setSortModel] = useState([
    { field: 'prjSeq', sort: 'desc' }
  ]);
  // 행 선택 상태 관리
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const makeFilters = (filters) => {

    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';


      const searchData = {
      useYn: 'Y',
      cstGrade: filters.searchGrade,
      prjTyp: filters.searchPrjTyp,
      // prjImptsList: filters.searchPrjImpt,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
      // 서버는 crtNm=작성자명, crtDptNm=작성부서명으로 LIKE 검색한다.
      // 기존에는 영업부서를 crtNm(사람 이름)으로, 영업사원을 crtDptNm(부서명)으로 보내 서로 뒤바뀌어 있었다.
      crtDptNm: filters.searchDpt ? filters.searchDpt : '',
      crtNm: filters.searchUsr ? filters.searchUsr : '',
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listBaseChances(makeFilters(defaultFilters)));
  }, [defaultFilters]);

  useEffect(() => {
    if (chances) {
      setTableData(chances);
    }
  }, [chances]);

  const handleFilters = (name, value) => {
    const newFilters = { ...defaultFilters, [name]: value };
    dispatch(setBaseFilters(newFilters));
  };

  // 행 클릭 핸들러 개선
  const handleRowClick = useCallback((params) => {
    if (params && params.id) {
      try {
        const cstGrade = params.row?.cstGrade || '';
        navigate(`/chancemng/${params.id}/${cstGrade}/1`);
      } catch (error) {
        console.error('Row click error:', error);
      }
    }
  }, [navigate]);

  const handleAddForecast = () => {
    navigate('/chancemng/addForecast');
  };

  const handleFirstForecast = () => {
    navigate('/chancemng/add');
  };

  const apiRef = useGridApiRef();

  // CSV 내보내기 옵션
  const csvOptions = {
    fileName: `포캐스팅_리스트_${dayjs().format('YYYY-MM-DD')}`,
    utf8WithBom: true,
    delimiter: ',',
    includeHeaders: true
  };

  // 짝수/홀수 행 구분을 위한 getRowClassName 함수
  const getRowClassName = (params) => {
    return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
  };

  // 이익률에 따른 색상 처리를 위한 함수
  const getAmountCellClassName = (params) => {
    if (!params || params.field !== 'totalMargin' || !params.value) {
      if (['totalSales', 'totalPurchase', 'totalProfit', 'totalMargin'].includes(params.field)) {
        return 'amount-cell';
      }
      return '';
    }
    
    const value = parseFloat(params.value);
    if (value > 20) return 'amount-cell positive-amount';
    if (value < 10) return 'amount-cell negative-amount';
    return 'amount-cell';
  };

  // 컬럼 정의 개선
  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        type: 'string',
        editable: false,
        disableExport: true,
        width: 100,
      },
      {
        field: 'prjSeq',
        headerName: 'No',
        minWidth: 80,
        flex: 0.5,
        type: 'number',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'super-app-theme--header',
        filterable: true,
      },
      {
        field: 'cstStatus',
        headerName: '단계',
        minWidth: 130,
        flex: 0.8,
        type: 'singleSelect',
        valueOptions: ['영업기회', '제안', '계약', '종료', '실패'],
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'super-app-theme--header',
        editable: false,
        filterable: true,
        renderCell: (params) => {
          if (!params || !params.value) return null;
          return <CstStatusChip {...params} />;
        },
      },
      {
        field: 'crtDptNm',
        headerName: '부서',
        minWidth: 120,
        flex: 0.6,
        type: 'string',
        editable: false,
        headerClassName: 'super-app-theme--header',
        filterable: true,
      },
      {
        field: 'crtNm',
        headerName: '영업담당자',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        headerClassName: 'super-app-theme--header',
        filterable: true,
        valueGetter: (params) => {
          if (!params || !params.row) return '';
          return `${params.row.crtNm || ''} ${params.row.crtGradeNm || ''}`;
        },
        renderCell: (params) => {
          if (!params || !params.row) return null;
          return (
            <Box>
                {params.row.crtNm || ''} {params.row.crtGradeNm || ''}
            </Box>
          );
        },
      },
      {
        field: 'vendorCmpNm',
        headerName: '매출처명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        headerClassName: 'super-app-theme--header',
        editable: false,
        filterable: true,
        valueGetter: (params) => {
          if (!params || !params.row) return '';
          return params.row.vendorCmpNm || params.row.partnerInc || '';
        },
        renderCell: (params) => {
          if (!params || !params.row) return null;
          return params.row.vendorCmpNm || params.row.partnerInc || '';
        },
      },
      {
        field: 'prjNo',
        headerName: '프로젝트 번호',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'super-app-theme--header',
        filterable: true,
        // 번호 옆에서 바로 공유 주소를 복사한다. 행 클릭과 겹치지 않게 아이콘이 이벤트를 삼킨다.
        renderCell: (params) => (params?.row?.prjNo ? (
          <ShareLinkBox
            compact
            text={params.row.prjNo}
            path={`/go/${params.row.prjNo}`}
          />
        ) : null),
      },
      {
        field: 'crtDt',
        headerName: '등록일자',
        minWidth: 120,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'super-app-theme--header',
        filterable: true,
        valueFormatter: (params) => {
          if (!params || !params) return '';
          return dayjs(params).format('YYYY-MM-DD');
        },
      },
      {
        field: 'expDay',
        headerName: '매출월',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'super-app-theme--header',
        filterable: true,
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 180,
        flex: 1,
        description: '',
        editable: false,
        headerClassName: 'super-app-theme--header',
        filterable: true,
        renderCell: (params) => (
          <>
            {params.value}
            <IconButton
              aria-label="preview"
              color="success"
              size="small"
              onClick={() => handleRowClick(params)}
            >
              <PreviewIcon />
            </IconButton>
          </>
        ),

      },
      {
        field: 'prjTyp',
        headerName: '타입',
        minWidth: 100,
        flex: 0.4,
        type: 'singleSelect',
        valueOptions: [
          { value: 'M', label: '유지보수' },
          { value: 'P', label: '프로젝트' }
        ],
        editable: false,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'super-app-theme--header',
        filterable: true,
        valueFormatter: (params) => {
          if (!params || params.value === undefined || params.value === null) return '';
          return params.value === 'M' ? '유지보수' : '프로젝트';
        },
        renderCell: (params) => {
          if (!params || params.value === undefined || params.value === null) return '';
          const isMaintenace = params.value === 'M';
          return (
            <Chip 
              label={isMaintenace ? '유지보수' : '프로젝트'}
              size="small"
              color={isMaintenace ? 'info' : 'default'}
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'prjImpt',
        headerName: '중요도',
        minWidth: 100,
        flex: 0.4,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'super-app-theme--header',
        filterable: true,
        renderCell: (params) => {
          if (!params || !params.value) return '';
          let color = 'default';
          if (params.value === 'H') color = 'error';
          else if (params.value === 'M') color = 'warning';
          else if (params.value === 'L') color = 'success';
          
          return (
            <Chip 
              label={params.value}
              size="small"
              color={color}
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'totalSales',
        headerName: '총매출액',
        minWidth: 120,
        flex: 0.6,
        description: '',
        type: 'number',
        align: 'right',
        headerAlign: 'center',
        headerClassName: 'super-app-theme--header',
        editable: false,
        filterable: true,
        valueFormatter: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
        renderCell: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
      },
      {
        field: 'totalPurchase',
        headerName: '총매입액',
        minWidth: 120,
        flex: 0.6,
        description: '',
        type: 'number',
        align: 'right',
        headerAlign: 'center',
        headerClassName: 'super-app-theme--header',
        editable: false,
        filterable: true,
        valueFormatter: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
        renderCell: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
      },
      {
        field: 'totalProfit',
        headerName: '매출이익',
        minWidth: 120,
        flex: 0.6,
        description: '',
        type: 'number',
        align: 'right',
        headerAlign: 'center',
        headerClassName: 'super-app-theme--header',
        editable: false,
        filterable: true,
        valueFormatter: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
        renderCell: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
      },
      {
        field: 'totalMargin',
        headerName: '이익률',
        minWidth: 90,
        flex: 0.4,
        description: '',
        type: 'number',
        align: 'right',
        headerAlign: 'center',
        headerClassName: 'super-app-theme--header',
        editable: false,
        filterable: true,
        valueFormatter: (params) => {
          if (!params || params.value === undefined) return '0%';
          return `${params.value}%`;
        },
        renderCell: (params) => {
          if (!params || params.value === undefined) return '0%';
          return (
            <Typography
              variant="body2"
              color={
                parseFloat(params.value) > 20
                  ? 'success.main'
                  : parseFloat(params.value) < 10
                  ? 'error.main'
                  : 'text.primary'
              }
              fontWeight="bold"
            >
              {params.value}
            </Typography>
          );
        },
      },
    ],
    [],
  );

  let initialValues = {
    searchPrjImpt: defaultFilters.searchPrjImpt,
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchPrjImpt: yup.array().nullable(),
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '등록 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('등록 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        const searchData = {
          useYn: 'Y',
          searchGrade: defaultFilters.searchGrade,
          searchPrjTyp: defaultFilters.searchPrjTyp,
          searchPrjImpt: values.searchPrjImpt,
          searchKeyword: values.searchKeyword,
          searchWords: values.searchWords,
          searchStrtDt: values.searchStrtDt,
          searchEndDt: values.searchEndDt,
          searchDpt: values.searchDpt,
          searchUsr: values.searchUsr,
          
        };
        dispatch(setBaseFilters(searchData));

        setSubmitting(false);
      }, 1000);
    },
  });

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: { xs: 2, md: 0 } }}>
      <BreadcrumbsContainer
        title="영업/영업 진행 현황/포캐스팅 리스트"
        icon="chance"
      />
     
      <ChanceListBox
        formik={formik}
       // handleAddForecast={handleAddForecast}
        isAdd
      />
      <ChanceStatusList filters={defaultFilters} onFilters={handleFilters} />
      <ChanceTotalSummary totals={totals} />
      
      <ChanceList
        apiRef={apiRef}
        columns={columns}
        rows={tableData}
        error={error}
        loading={loading}
        handleRowClick={handleRowClick}
        
        columnVisibilityModel={{
          id: false,
        }}
		
      />

    </Box>
  );
};

export default ChanceListContainer;
